import { createContext, use, type ReactNode } from "react";

/**
 * Dónde se está dibujando el chat.
 *
 * El mismo hilo se ve en dos sitios: a pantalla completa en el modo Chat, y en
 * la ventana pequeña de Work. No son dos chats --eso sería el error (§9.2)--
 * sino el mismo con dos anchos, y hay componentes del agente que en 380px no
 * caben: una tabla de ocho columnas, un gráfico, un documento largo.
 *
 * Esto es lo que les permite saberlo SIN que nadie los recorte por fuera. Un
 * componente cortado se lee a medias y engaña; uno que dice "esto es una tabla
 * de tal cosa, ábrela en el chat" no engaña a nadie.
 */
export type ChatDensity = "full" | "compact";

interface ChatSurface {
  readonly density: ChatDensity;
  /**
   * Saltar al modo Chat y dejar a la vista ESE mensaje.
   *
   * Solo lo hay en la ventana pequeña: a pantalla completa ya se está ahí.
   */
  readonly openInChat?: ((messageId: string) => void) | undefined;
}

const ChatSurfaceContext = createContext<ChatSurface>({ density: "full" });

export const useChatSurface = (): ChatSurface => use(ChatSurfaceContext);

export function ChatSurfaceProvider({
  density,
  openInChat,
  children,
}: ChatSurface & { readonly children: ReactNode }) {
  return (
    <ChatSurfaceContext value={{ density, openInChat }}>
      {children}
    </ChatSurfaceContext>
  );
}

/**
 * El mensaje dentro del que se está dibujando algo.
 *
 * Lo pone `Message` y lo lee quien necesita señalar su propio sitio --hoy, la
 * tarjeta compacta que ofrece "ver en el chat"--. Viaja por contexto y no por
 * props para no cambiarle la forma a todos los componentes del registro por
 * algo que solo usa uno.
 */
const MessageIdContext = createContext<string | null>(null);

export const useMessageId = (): string | null => use(MessageIdContext);

export function MessageIdProvider({
  messageId,
  children,
}: {
  readonly messageId: string;
  readonly children: ReactNode;
}) {
  return <MessageIdContext value={messageId}>{children}</MessageIdContext>;
}

/** El ancla en el DOM de un mensaje, para poder saltar a él. */
export const messageAnchor = (messageId: string): string =>
  `message-${messageId}`;
